import React, { useState, useEffect } from 'react';
import { Camera, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { resolveAvatarUrl } from '../lib/utils';
import BottomNav from '../components/BottomNav';
import StoriesBar from '../components/StoriesBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UpdatesPage = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateStory, setShowCreateStory] = useState(false);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const res = await axios.get(`${API}/stories`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStories(res.data || []);
      } catch {
        setStories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, [token]);

  // Group stories by user
  const myStories = stories.filter(s => s.user_id === user?.id);
  const otherStories = stories.filter(s => s.user_id !== user?.id);

  // Group other stories by user
  const groupedStories = {};
  otherStories.forEach(s => {
    if (!groupedStories[s.user_id]) {
      groupedStories[s.user_id] = { user: s, stories: [] };
    }
    groupedStories[s.user_id].stories.push(s);
  });

  return (
    <div className={`h-screen flex flex-col pb-14 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <div className={`px-4 pt-12 pb-3 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`} data-testid="updates-page-title">
          Updates
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* My Status */}
        <div className={`px-4 py-3 ${isDarkMode ? '' : ''}`}>
          <p className={`text-xs font-medium uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Status
          </p>
          <div className="flex items-center gap-3">
            <div className="relative">
              {user?.avatar_url ? (
                <img 
                  src={resolveAvatarUrl(user.avatar_url)} 
                  alt="My status" 
                  className="w-14 h-14 rounded-full object-cover"
                  onError={(e) => { 
                    e.target.style.display = 'none'; 
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex'; 
                    }
                  }}
                />
              ) : null}
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium text-xl"
                style={user?.avatar_url ? { display: 'none' } : {}}>
                {(user?.display_name || '?').charAt(0).toUpperCase()}
              </div>
              <button
                onClick={() => setShowCreateStory(true)}
                className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center border-2 border-white dark:border-gray-900"
                data-testid="add-status-btn"
              >
                <Plus size={14} className="text-white" />
              </button>
            </div>
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>My status</p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {myStories.length > 0 ? `${myStories.length} update${myStories.length > 1 ? 's' : ''}` : 'Tap to add status update'}
              </p>
            </div>
          </div>
        </div>

        {/* Recent updates */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-[#D4AF37]" />
          </div>
        ) : Object.keys(groupedStories).length > 0 ? (
          <div className="px-4 py-3">
            <p className={`text-xs font-medium uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Recent updates
            </p>
            {Object.values(groupedStories).map(({ user: storyUser, stories: userStories }) => (
              <div key={storyUser.user_id} className={`flex items-center gap-3 py-3 ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} rounded-xl px-2 -mx-2 cursor-pointer`}>
                <div className="relative">
                  <div className={`w-14 h-14 rounded-full p-0.5 ${userStories.some(s => !s.viewed) ? 'bg-gradient-to-br from-[#D4AF37] to-[#C5A059]' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                    <div className={`w-full h-full rounded-full overflow-hidden border-2 ${isDarkMode ? 'border-gray-900' : 'border-white'}`}>
                      {storyUser.avatar_url ? (
                        <img src={resolveAvatarUrl(storyUser.avatar_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                          {(storyUser.display_name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {storyUser.display_name || storyUser.username || 'Unknown'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {userStories.length} update{userStories.length > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <Camera size={32} className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
            </div>
            <p className={`text-base font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>No updates yet</p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Status updates from your contacts will appear here</p>
          </div>
        )}
      </div>

      {showCreateStory && <StoriesBar onClose={() => setShowCreateStory(false)} />}
      <BottomNav />
    </div>
  );
};

export default UpdatesPage;
